import React, { useState, useEffect, useCallback } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { TouchableOpacity } from "react-native";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import { View, StyleSheet, Text } from "react-native-web";
import EventStore from "../../storage/eventStore";
import { parseUTCToLocal } from "../../utils/helper";
import { COLORS, STRINGS } from "../../constants";

const CalendarModal = ({ onClose }) => {
  const setFilterDates = EventStore((state) => state.setFilterDates);
  const selectedFromDate = EventStore((state) => state.selectedFromDate);
  const selectedToDate = EventStore((state) => state.selectedToDate);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedValue, setSelectedValue] = useState(null);

  useEffect(() => {
    if (selectedFromDate && selectedToDate) {
      const from = new Date(selectedFromDate);
      const to = new Date(selectedToDate);
      setStartDate(parseUTCToLocal(from.toDateString()));
      setEndDate(parseUTCToLocal(to.toDateString()));
      setRange([from, to]);
      setActiveStartDate(from);
    }
  }, [selectedFromDate, selectedToDate]);

  const today = new Date();
  const maxDate = new Date(today);
  const daysToAdd = 365;
  maxDate.setDate(maxDate.getDate() + daysToAdd);

  const [range, setRange] = useState([today, today]);
  const [activeStartDate, setActiveStartDate] = useState(today);

  const onDateChange = (selected) => {
    if (Array.isArray(selected)) {
      const [start, end] = selected;
      const endDate = end?.toDateString() || start.toDateString();
      setStartDate(parseUTCToLocal(start.toDateString()));
      setEndDate(parseUTCToLocal(endDate));
    } else {
      const dateSelected = parseUTCToLocal(selected.toDateString());
      setStartDate(dateSelected);
      setEndDate(dateSelected);
    }
    setRange(selected);
  };

  const onClickDay = (date) => {
    setStartDate(null);
    setEndDate(null);
    setSelectedValue(date);
    if (selectedFromDate && selectedToDate) {
      setRange([date, date]);
    }
  };

  const tileDisabled = useCallback(
    ({ date, view }) => {
      if (view !== "month") return false;

      const localDate = parseUTCToLocal(date.toDateString());
      return localDate < parseUTCToLocal(today.toDateString()) || localDate > parseUTCToLocal(maxDate.toDateString());
    },
    [today, maxDate]
  );

  const tileClassName = useCallback(
    ({ date, view }) => {
      if (view !== "month") return "";

      const currentDate = new Date(date);
      const start = range?.[0];
      const end = range?.[1];

      if (currentDate < today || currentDate > maxDate) {
        return "disabled-tile";
      }

      if (start && end && currentDate > new Date(start) && currentDate < parseUTCToLocal(end.toDateString())) {
        return "custom-range";
      }

      return "enabled-tile";
    },
    [range, today, maxDate]
  );

  const navigationLabel = ({ date }) => (
    <span
      className={`${isPrevDisabled() ? "label-padding-left" : ""} ${isNextDisabled() ? "label-padding-right" : ""}`}
    >
      {date.toLocaleString("default", { month: "long" })} {date.getFullYear()}
    </span>
  );

  const isPrevDisabled = () =>
    activeStartDate.getFullYear() === today.getFullYear() && activeStartDate.getMonth() === today.getMonth();
  const isNextDisabled = () =>
    activeStartDate.getFullYear() === maxDate.getFullYear() && activeStartDate.getMonth() === maxDate.getMonth();

  return (
    <View style={styles.calendarWrapper}>
      <style>{`
        .react-calendar {
          background-color: ${COLORS.textLight};
          border: none;
          width: 70vw;
          font-size: 18px;
        }
        .react-calendar__tile:enabled {
          color: ${COLORS.textDarker};
        }
        .react-calendar__navigation__arrow {
          font-size: 21px;
        }
        .react-calendar__tile:disabled {
          color: ${COLORS.border};
          background-color: ${COLORS.disabledCalendarText};
        }
        .react-calendar__navigation__label {
          font-weight: bold;
          font-size: 21px;
          pointer-events: none;
          color: black;
          cursor: default;
        }
        .label-padding-left {
          padding-left: 40px; 
        }
        .label-padding-right {
          padding-right: 40px;
        }
        .react-calendar__tile--now {
          background: transparent !important;
        }
        .react-calendar__tile--now:hover,
        .react-calendar__tile--now:focus {
          background: ${COLORS.backgroundGreen};
          color: ${COLORS.textDarker};
        }
        .react-calendar__tile:enabled:hover {
          background-color: transparent;
        }
        .react-calendar__tile--range {
          background: ${COLORS.backgroundGreen};
          color: ${COLORS.textLight};
        }
        .react-calendar__tile--active,
        .react-calendar__tile--rangeStart,
        .react-calendar__tile--rangeEnd {
          background: ${COLORS.primary} !important;
          color: ${COLORS.textLight} !important;
        }
        .custom-range {
          background: ${COLORS.backgroundGreen} !important;
          color: ${COLORS.textDarker} !important;
        }
      `}</style>
      <TouchableOpacity accessibilityLabel={STRINGS.closeDateFilter} onPress={onClose} style={styles.closeButton}>
        <FontAwesomeIcon icon={faCircleXmark} size={"2xl"} color={COLORS.border} />
      </TouchableOpacity>
      <Calendar
        selectRange
        onChange={onDateChange}
        onClickDay={onClickDay}
        value={range}
        tileDisabled={tileDisabled}
        tileClassName={tileClassName}
        onActiveStartDateChange={({ activeStartDate }) => setActiveStartDate(activeStartDate)}
        navigationLabel={navigationLabel}
        prevLabel={isPrevDisabled() ? null : "‹"}
        nextLabel={isNextDisabled() ? null : "›"}
        prev2Label={null}
        next2Label={null}
      />
      <View style={styles.buttonsContainer}>
        <TouchableOpacity
          accessibilityLabel={STRINGS.resetFilterDates}
          style={styles.buttonStyle}
          onPress={() => {
            setSelectedValue(null);
            setRange(null, null);
            setStartDate(null);
            setEndDate(null);
            setFilterDates(null, null);
          }}
        >
          <Text style={styles.buttonText}>{STRINGS.reset}</Text>
        </TouchableOpacity>
        <TouchableOpacity
          accessibilityLabel={STRINGS.applyFilterDates}
          style={styles.buttonStyle}
          onPress={() => {
            if (!startDate && !endDate) {
              setStartDate(selectedValue);
              setEndDate(selectedValue);
              setFilterDates(selectedValue, selectedValue);
            } else {
              setFilterDates(startDate, endDate);
            }
            onClose();
          }}
        >
          <Text style={styles.buttonText}>{STRINGS.apply}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  calendarWrapper: {
    paddingTop: 50,
    padding: 20,
    backgroundColor: COLORS.textLight,
    position: "relative",
  },
  closeButton: {
    position: "absolute",
    top: 0,
    right: 10,
  },
  buttonsContainer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
  },
  buttonStyle: {
    alignSelf: "flex-end",
    backgroundColor: COLORS.primary,
    paddingHorizontal: 30,
    paddingVertical: 12,
    marginTop: 18,
    borderRadius: 20,
    textAlign: "center",
  },
  buttonText: {
    color: COLORS.textLight,
    fontSize: 16,
    fontWeight: "bold",
  },
});

export default CalendarModal;
